import React, { useEffect, useRef, useState } from 'react'
import { Button, notification } from '@surecompanies/core_components'
import InputNumber from 'antd/lib/input-number'
import Input from 'antd/lib/input'
import { Switch } from 'antd'
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons'
import { FIELD_CONFIG, coerceParamValue } from '../utils/planBenefitFields'
import { fieldValueChanged } from '../utils/compareGridColumns'
import { upsertOverrideRuleField } from '../../PlanOverrides/api/plan-benefit-override-rules-api'
import { PlanBenefitOverrideValueSource } from '../../PlanOverrides/pages/PlanBenefitOverrides/enums/plan-benefit-override-rule.enum'

export interface PopoverCellInfo {
  field: string
  rowData: Record<string, unknown>
  anchor: HTMLElement
}

interface Props {
  cell: PopoverCellInfo | null
  onClose: () => void
  onOptimisticUpdate: (
    rowId: string,
    overrideParams: Record<string, unknown>,
    overrideStatus: string
  ) => void
  onRevert: (rowId: string, originalData: Record<string, unknown>) => void
  onCommit: (
    rowId: string,
    overrideParams: Record<string, unknown>,
    overrideStatus: string
  ) => void
}

const SRC = PlanBenefitOverrideValueSource

const currencyFormatter = (v: number | string | undefined): string => {
  if (v === undefined || v === null || v === '') return ''
  const n = typeof v === 'string' ? Number(v) : v
  if (isNaN(n)) return String(v)
  return n.toLocaleString('en-US')
}

const currencyParser = (v: string | undefined): number | string => {
  if (!v) return ''
  const s = v.replace(/[$,\s]/g, '')
  const n = Number(s)
  return isNaN(n) ? s : n
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function resolveDefaultSource(
  cell: PopoverCellInfo,
  fieldHasChange: boolean,
  hasRealData: boolean
): PlanBenefitOverrideValueSource {
  const paramSources = cell.rowData.__paramSources as Record<
    string,
    string
  > | null
  if (paramSources?.[cell.field]) {
    const src = paramSources[cell.field] as PlanBenefitOverrideValueSource
    if (src === SRC.IDEON_DATA && !fieldHasChange && hasRealData)
      return SRC.REAL_DATA
    return src
  }
  const overrideParams = cell.rowData.__overrideParams as Record<
    string,
    unknown
  > | null
  if (overrideParams?.[cell.field] !== undefined) {
    return SRC.OVERRIDE_DATA
  }
  // Ideon is hidden on white cells (no change, live data exists) — default to Real
  if (!fieldHasChange && hasRealData) return SRC.REAL_DATA
  return SRC.IDEON_DATA
}

function resolveOverrideDefault(
  cell: PopoverCellInfo
): string | number | boolean | null {
  const overrideParams = cell.rowData.__overrideParams as Record<
    string,
    unknown
  > | null
  const v = overrideParams?.[cell.field] ?? cell.rowData[cell.field] ?? null
  return v as string | number | boolean | null
}

export function PlanBenefitCellPopover({
  cell,
  onClose,
  onOptimisticUpdate,
  onRevert,
  onCommit,
}: Props) {
  const cfg = cell ? FIELD_CONFIG[cell.field] : null

  const [source, setSource] = useState<PlanBenefitOverrideValueSource>(
    SRC.IDEON_DATA
  )
  const [overrideValue, setOverrideValue] = useState<
    string | number | boolean | null
  >(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!cell) return
    const ideonVal = cell.rowData[cell.field]
    const liveData = cell.rowData._currentValue as Record<
      string,
      unknown
    > | null
    const realVal = liveData?.[cell.field]
    const hasReal = realVal !== null && realVal !== undefined && realVal !== ''
    const hasChange = hasReal && fieldValueChanged(ideonVal, realVal)
    setSource(resolveDefaultSource(cell, hasChange, hasReal))
    setOverrideValue(resolveOverrideDefault(cell))
  }, [cell?.field, cell?.rowData])

  useEffect(() => {
    if (!cell) return
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [cell, onClose])

  useEffect(() => {
    if (!cell) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [cell, onClose])

  const [pos, setPos] = useState({ top: 0, left: 0 })
  useEffect(() => {
    if (!cell) return
    const update = () => {
      const r = cell.anchor.getBoundingClientRect()
      const popoverHeight = containerRef.current?.offsetHeight ?? 320
      const fitsBelow = r.bottom + 6 + popoverHeight < window.innerHeight - 8
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - 324)),
        top: fitsBelow ? r.bottom + 6 : Math.max(8, r.top - popoverHeight - 6),
      })
    }
    update()
    window.addEventListener('scroll', update, true)
    return () => window.removeEventListener('scroll', update, true)
  }, [cell])

  if (!cell || !cfg) return null

  const ideonValue = cell.rowData[cell.field]
  const liveData = cell.rowData._currentValue as Record<string, unknown> | null
  const realValue = liveData?.[cell.field]
  const hasRealData =
    realValue !== null && realValue !== undefined && realValue !== ''
  const fieldHasChange = hasRealData && fieldValueChanged(ideonValue, realValue)

  const { top, left } = pos

  const handleSave = async () => {
    const rowId = cell.rowData.id as string
    const effectiveValue =
      source === SRC.IDEON_DATA
        ? ideonValue
        : source === SRC.REAL_DATA
          ? realValue
          : overrideValue
    const coerced = coerceParamValue(cell.field, effectiveValue)
    const currentOverrideParams =
      (cell.rowData.__overrideParams as Record<string, unknown>) ?? {}
    const optimisticParams = { ...currentOverrideParams, [cell.field]: coerced }

    onOptimisticUpdate(
      rowId,
      optimisticParams,
      (cell.rowData.__overrideStatus as string) ?? 'DRAFT'
    )
    onClose()

    try {
      const rule = await upsertOverrideRuleField({
        match: {
          hiosPlanId: cell.rowData.hiosPlanId as string,
          year: cell.rowData.year as number,
          benefitName: cell.rowData.benefit as string,
        },
        field: cell.field,
        value: coerced,
        source,
      })
      onCommit(rowId, rule.params as Record<string, unknown>, rule.status)
    } catch {
      onRevert(rowId, cell.rowData)
      notification.error({
        message: 'Failed to save override. Please try again.',
      })
    }
  }

  const renderValueDisplay = (v: unknown) => {
    if (cfg.valueType === 'boolean') {
      return <Switch checked={Boolean(v)} disabled size="small" />
    }
    return (
      <span style={{ fontSize: 13, color: '#374151' }}>{formatValue(v)}</span>
    )
  }

  const renderOverrideInput = () => {
    const disabled = source !== SRC.OVERRIDE_DATA
    if (cfg.valueType === 'boolean') {
      return (
        <>
          <Switch
            checked={overrideValue === true}
            disabled={disabled}
            onChange={(checked) => setOverrideValue(checked)}
            size="small"
          />
        </>
      )
    }
    if (cfg.valueType === 'currency') {
      return (
        <InputNumber
          value={overrideValue as number | null}
          disabled={disabled}
          onChange={(v) => setOverrideValue(v ?? null)}
          formatter={currencyFormatter}
          parser={
            currencyParser as unknown as (v: string | undefined) => number
          }
          size="small"
          style={{ width: '100%' }}
          min={-1}
          autoFocus={source === SRC.OVERRIDE_DATA}
        />
      )
    }
    if (cfg.valueType === 'number') {
      return (
        <InputNumber
          value={overrideValue as number | null}
          disabled={disabled}
          onChange={(v) => setOverrideValue(v ?? null)}
          size="small"
          style={{ width: '100%' }}
          min={-1}
          autoFocus={source === SRC.OVERRIDE_DATA}
        />
      )
    }
    return (
      <Input
        value={(overrideValue as string) ?? ''}
        disabled={disabled}
        onChange={(e) => setOverrideValue(e.target.value)}
        size="small"
        autoFocus={source === SRC.OVERRIDE_DATA}
        onPressEnter={source === SRC.OVERRIDE_DATA ? handleSave : undefined}
      />
    )
  }

  const blockStyle = (
    src: PlanBenefitOverrideValueSource
  ): React.CSSProperties => ({
    border: `1.5px solid ${source === src ? '#a7f3d0' : '#e5e7eb'}`,
    borderRadius: 7,
    padding: '8px 10px',
    background: source === src ? '#ecfdf5' : '#fafafa',
    cursor: 'pointer',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  })

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    minWidth: 48,
    flexShrink: 0,
  }

  const checkStyle: React.CSSProperties = {
    marginLeft: 'auto',
    color: '#16a34a',
    fontSize: 13,
    flexShrink: 0,
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top,
        left,
        width: 320,
        zIndex: 1050,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
          {cfg.label}
        </span>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ padding: 0, height: 20 }}
        />
      </div>

      {/* Ideon block — only shown when field has a diff or there's no live data */}
      {(fieldHasChange || !hasRealData) && (
        <div
          style={blockStyle(SRC.IDEON_DATA)}
          onClick={() => setSource(SRC.IDEON_DATA)}
        >
          <span style={labelStyle}>Ideon</span>
          {renderValueDisplay(ideonValue)}
          {source === SRC.IDEON_DATA && (
            <CheckCircleFilled style={checkStyle} />
          )}
        </div>
      )}

      {/* Real block — only if live data exists */}
      {hasRealData && (
        <div
          style={blockStyle(SRC.REAL_DATA)}
          onClick={() => setSource(SRC.REAL_DATA)}
        >
          <span style={labelStyle}>Sureco</span>
          {renderValueDisplay(realValue)}
          {source === SRC.REAL_DATA && <CheckCircleFilled style={checkStyle} />}
        </div>
      )}

      {/* Override block */}
      <div
        style={blockStyle(SRC.OVERRIDE_DATA)}
        onClick={() => setSource(SRC.OVERRIDE_DATA)}
      >
        <span style={labelStyle}>Change Record</span>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            pointerEvents: source === SRC.OVERRIDE_DATA ? undefined : 'none',
          }}
        >
          {renderOverrideInput()}
        </div>
        {source === SRC.OVERRIDE_DATA && (
          <CheckCircleFilled style={checkStyle} />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          justifyContent: 'flex-end',
          marginTop: 4,
        }}
      >
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={handleSave}
          style={{ background: '#0d9488', borderColor: '#0d9488' }}
        >
          Override
        </Button>
      </div>
    </div>
  )
}
